import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useRef, useState, type FC } from 'react';
import { Text } from './text';

interface ExpandableTextProps {
  children: string;
  maxLines?: number;
  className?: string;
}

export const ExpandableText: FC<ExpandableTextProps> = ({
  children,
  maxLines = 4,
  className,
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const checkTruncation = () => {
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 20;
      const maxHeight = lineHeight * maxLines;
      setIsTruncated(element.scrollHeight > maxHeight + 1);
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [children, maxLines]);

  return (
    <div className="flex flex-col gap-2">
      <Text
        ref={textRef}
        className={cn('text-sm leading-normal', className)}
        style={
          !isExpanded
            ? {
                display: '-webkit-box',
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'pre-line',
              }
            : {
                whiteSpace: 'pre-line',
              }
        }
      >
        {children}
      </Text>
      {isTruncated && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1.5 text-xs text-black self-end"
        >
          {isExpanded ? (
            <>
              <Text variant="body-xs" className="font-medium">
                View less
              </Text>
              <ChevronUp className="w-4 h-4" />
            </>
          ) : (
            <>
              <Text variant="body-xs" className="font-medium">
                View more
              </Text>
              <ChevronDown className="w-3 h-3" />
            </>
          )}
        </button>
      )}
    </div>
  );
};
